import type { Caption, Word } from '@/types/caption'
import { countCaptionLines } from '@/lib/caption-layout'

/**
 * The one caption chunker. Words are timed by their START (`timelineIn`) and real end
 * (`timelineOut`), in ms. The backend mirrors these rules in
 * `backend/altar/common/utils/clips/editing_script.py` (group_caption_words, time_caption_groups):
 * change both together.
 */
export interface CaptionTimedWord {
  text: string
  timelineIn: number
  timelineOut: number
}

export interface CaptionGroupLimits {
  targetWords: number
  maxWords: number
  maxChars: number
  maxSpanMs: number
  maxLines?: number | null
  charsPerLine?: number
}

export interface CaptionGroupTiming {
  startMs: number
  endMs: number
}

/** A pause this long between two words always starts a new caption. */
export const CAPTION_PAUSE_MS = 300
/** A caption shorter than this absorbs the next word instead of closing. */
export const CAPTION_MIN_GROUP_MS = 400
/** A gap shorter than this keeps the caption up until the next one starts. */
export const CAPTION_GAP_FILL_MS = 500
/** Otherwise a caption lingers this long after its last word... */
export const CAPTION_TAIL_HOLD_MS = 200
/** ...is on screen at least this long... */
export const CAPTION_MIN_SHOW_MS = 500
/** ...and never lingers more than this past its last word. */
export const CAPTION_MAX_HOLD_MS = 800
/** A word timed with no length still spans this much. */
export const CAPTION_MIN_WORD_MS = 120
/** The highlight comes on this much before the word is spoken. */
export const CAPTION_HIGHLIGHT_LEAD_MS = 60

/** Filler words hidden from captions (they stay in the transcript). */
export const FILLER_WORDS = ["um", "umm", "uh", "uhh", "uhm", "er", "erm", "ah", "hmm", "mm", "mhm"]

const FUNCTION_WORDS = new Set([
  "a", "an", "the", "and", "or", "but", "nor", "of", "to", "in", "on", "for", "with", "at", "by",
  "from", "that", "so", "if", "as", "is", "was", "are", "my", "your", "our", "their", "his", "her",
  "its", "i", "you", "we", "they", "he", "she", "i'm", "it's", "because", "than", "into", "onto",
])
const ABBREVIATIONS = new Set(["mr.", "mrs.", "ms.", "dr.", "st.", "vs.", "jr.", "sr.", "prof."])
const NUMBER_UNITS = /^(%|percent|hundred|thousand|million|billion|trillion|k|m|bn)\b/i

const bare = (text: string) => text.trim().replace(/^["'(\[]+|["')\]]+$/g, "")
const cleanWord = (text: string) => text.toLowerCase().replace(/[^a-z0-9']/g, "")

/** "um" or "uh" is a filler; one said as an exclamation ("ah!") is a word and stays. */
export function isFillerWord(text: string): boolean {
  return !text.trim().endsWith("!") && FILLER_WORDS.includes(text.toLowerCase().replace(/[^a-z']/g, ""))
}

function isAbbreviation(text: string): boolean {
  return ABBREVIATIONS.has(bare(text).toLowerCase())
}

function endsSentence(text: string): boolean {
  return /[.?!…]$/.test(bare(text)) && !isAbbreviation(text)
}

function endsClause(text: string): boolean {
  return /[,;:—–]$/.test(bare(text))
}

/** Whisper splits "99.0%" into "99" + ".0%"; a piece like ".0%" or ",000" belongs to the word before. */
function isContinuation(text: string): boolean {
  return /^[.,%]\S|^%$/.test(text.trim())
}

function isCapitalized(text: string): boolean {
  const word = bare(text)
  return /^[A-Z]/.test(word) && !/^I('|$)/.test(word)
}

/** Numbers stay with their unit or continuation, names with the rest of the name. */
function keepsTogether(word: CaptionTimedWord, next: CaptionTimedWord): boolean {
  if (isContinuation(next.text)) return true
  if (/\d$/.test(bare(word.text)) && NUMBER_UNITS.test(bare(next.text))) return true
  const openName = isAbbreviation(word.text) || (!endsClause(word.text) && !endsSentence(word.text))
  return openName && isCapitalized(word.text) && isCapitalized(next.text)
}

/**
 * Group limits for a caption style: one line holds 2 words (3 at most, 16 characters); each extra
 * line allowed by the "Lines per caption" setting adds as much again. wordsPerCaption caps the count.
 */
export function captionGroupLimits(
  wordsPerCaption: number | null | undefined,
  maxLines?: number | null,
  charsPerLine?: number,
): CaptionGroupLimits {
  const lines = Math.max(1, Math.min(3, maxLines ?? 1))
  const cap = wordsPerCaption && wordsPerCaption > 0 ? Math.round(wordsPerCaption) : 3 * lines
  const maxWords = Math.max(1, Math.min(3 * lines, cap))
  return {
    targetWords: Math.min(2 * lines, maxWords),
    maxWords,
    maxChars: 16 * lines,
    maxSpanMs: 1400 * lines,
    maxLines,
    charsPerLine,
  }
}

function overflows(group: CaptionTimedWord[], next: CaptionTimedWord, limits: CaptionGroupLimits): boolean {
  const texts = [...group, next].map((word) => word.text)
  return (
    texts.length > limits.maxWords ||
    texts.join(" ").length > limits.maxChars ||
    next.timelineOut - group[0].timelineIn > limits.maxSpanMs ||
    (!!limits.maxLines && !!limits.charsPerLine && countCaptionLines(texts, limits.charsPerLine) > limits.maxLines)
  )
}

/**
 * Splits start-sorted words into caption groups: always after . ? !, on a pause of 300 ms or more,
 * after , ; : once a group has 2 words, at the group's word target once it lasts 400 ms, and never
 * past its limits. A group never ends on a function word or inside a number or a name when that
 * word can move to the next group instead. Filler words are left out.
 */
export function chunkCaptionWords<T extends CaptionTimedWord>(words: T[], limits: CaptionGroupLimits): T[][] {
  const spoken = words.filter((word) => !isFillerWord(word.text))
  const groups: T[][] = []
  let group: T[] = []
  const close = () => {
    groups.push(group)
    group = []
  }
  spoken.forEach((word, index) => {
    group.push(word)
    const next = spoken[index + 1]
    if (!next) return
    const together = keepsTogether(word, next)
    const longEnough = word.timelineOut - group[0].timelineIn >= CAPTION_MIN_GROUP_MS
    if (!together && (endsSentence(word.text) || next.timelineIn - word.timelineOut >= CAPTION_PAUSE_MS)) return close()
    if (!together && endsClause(word.text) && group.length >= 2 && longEnough) return close()
    const dangles = (left: T, right: T) => keepsTogether(left, right) || FUNCTION_WORDS.has(cleanWord(left.text))
    if (overflows(group, next, limits)) {
      // A lone dangling word ("I", "to") stays with the next word even past the time span.
      if (group.length === 1 && dangles(word, next) && !overflows(group, next, { ...limits, maxSpanMs: Infinity })) return
      const carried: T[] = []
      while (group.length > 1 && dangles(group[group.length - 1], carried[0] ?? next)) carried.unshift(group.pop()!)
      // Carrying would strand a lone dangling word ("and" | "the European Union."): keep the group
      // whole and let it take one word past its limit instead.
      if (group.length === 1 && carried.length > 0 && dangles(group[0], carried[0]) && carried.length < limits.maxWords) {
        group.push(...carried)
        return
      }
      close()
      group.push(...carried)
      return
    }
    if (group.length >= limits.targetWords && longEnough && !dangles(word, next)) close()
  })
  if (group.length > 0) groups.push(group)
  return groups
}

/**
 * When each group is on screen: from its first word's start until 200 ms past its last word (at
 * least 500 ms on screen, at most 800 ms past the last word), held on until the next group starts
 * when the blank left would be under 500 ms (no blink). Groups never overlap or run past `endMs`.
 */
export function timeCaptionGroups(groups: CaptionTimedWord[][], endMs = Infinity): CaptionGroupTiming[] {
  return groups.map((group, index) => {
    const startMs = group[0].timelineIn
    const lastEndMs = Math.max(...group.map((word) => word.timelineOut))
    const nextStartMs = groups[index + 1]?.[0].timelineIn ?? Infinity
    const lingerMs = Math.min(Math.max(lastEndMs + CAPTION_TAIL_HOLD_MS, startMs + CAPTION_MIN_SHOW_MS), lastEndMs + CAPTION_MAX_HOLD_MS)
    // Bridge the gap when the blank left after lingering would be shorter than CAPTION_GAP_FILL_MS.
    const heldMs = nextStartMs - lingerMs < CAPTION_GAP_FILL_MS ? nextStartMs : lingerMs
    return { startMs, endMs: Math.max(startMs + 1, Math.min(heldMs, nextStartMs, endMs)) }
  })
}

/**
 * The one lit word of a caption group at `nowMs` (-1 for none): a word lights CAPTION_HIGHLIGHT_LEAD_MS
 * before it is spoken and stays lit until the next word lights, the last one until the group leaves.
 * Two words are never lit at once.
 */
export function activeCaptionWordIndex(words: Pick<CaptionTimedWord, "timelineIn">[], nowMs: number, groupEndMs: number): number {
  if (nowMs >= groupEndMs) return -1
  for (let index = words.length - 1; index >= 0; index -= 1) {
    if (nowMs >= words[index].timelineIn - CAPTION_HIGHLIGHT_LEAD_MS) return index
  }
  return -1
}

/** Caption text as shown: trailing , . ; : dropped, ? and ! kept. */
export function displayCaptionText(text: string): string {
  return text.replace(/[,.;:]+$/, "") || text
}

/** A transcript word as a caption word: its start, and its end (at least CAPTION_MIN_WORD_MS later). */
export function toCaptionTimedWord(word: Word): CaptionTimedWord & { word: Word } {
  const timelineIn = Math.max(0, Math.floor((word.start_time ?? word.time_stamp - 0.2) * 1000))
  return {
    text: word.word,
    timelineIn,
    timelineOut: Math.max(Math.floor(word.time_stamp * 1000), timelineIn + CAPTION_MIN_WORD_MS),
    word,
  }
}

/** Groups transcript words into timed captions with the shared chunker. */
export function generateCaptionsFromWords(
  words: Word[],
  wordsPerCaption: number,
  maxLines?: number | null,
): Caption[] {
  if (!words || words.length === 0) return []
  const timed = words.map(toCaptionTimedWord).sort((left, right) => left.timelineIn - right.timelineIn)
  const groups = chunkCaptionWords(timed, captionGroupLimits(wordsPerCaption, maxLines))
  const timings = timeCaptionGroups(groups)
  return groups.map((group, index) => ({
    id: `caption-${crypto.randomUUID()}`,
    text: group.map((item) => item.text).join(' '),
    startTime: timings[index].startMs / 1000,
    endTime: timings[index].endMs / 1000,
    words: group.map(({ word }) => ({
      word: word.word,
      time_stamp: word.time_stamp,
      start_time: word.start_time,
      start_position: word.start_position,
      end_position: word.end_position,
    })),
  }))
}

/**
 * Generate text shadow CSS for stroke effect
 * Uses multiple layers for smooth, filled stroke appearance
 */
export function generateTextStroke(
  strokeWidth: number,
  strokeColor: string
): string {
  if (strokeWidth <= 0) return 'none'

  const shadows: string[] = []

  // For thick strokes, use multiple rings at different radii for solid fill
  // More steps = smoother circle outline
  const steps = Math.max(16, strokeWidth * 2) // More steps for larger strokes

  // Create multiple concentric rings for filled appearance
  const rings = Math.max(1, Math.ceil(strokeWidth / 2))

  for (let ring = 1; ring <= rings; ring++) {
    const radius = (strokeWidth / rings) * ring
    for (let i = 0; i < steps; i++) {
      const angle = (i * 360) / steps
      const x = Math.cos((angle * Math.PI) / 180) * radius
      const y = Math.sin((angle * Math.PI) / 180) * radius
      shadows.push(`${x.toFixed(1)}px ${y.toFixed(1)}px 0 ${strokeColor}`)
    }
  }

  return shadows.join(', ')
}

/**
 * Word text corrections (editingScript.wordText): transcript word index -> corrected text. Timing
 * never changes; captions are regenerated from the corrected words.
 */
export type WordTextEdits = Record<string, string>

/** The clip's words with the user's text corrections applied. */
export function applyWordText(words: Word[], edits: WordTextEdits | undefined): Word[] {
  if (!edits || Object.keys(edits).length === 0) return words
  return words.map((word, index) => (edits[index] !== undefined ? { ...word, word: edits[index] } : word))
}

/**
 * The corrections after setting word `index` to `text`: blank text changes nothing, and text equal
 * to the original word drops the correction.
 */
export function setWordText(
  edits: WordTextEdits | undefined,
  words: Word[],
  index: number,
  text: string,
): WordTextEdits {
  const current = edits ?? {}
  const trimmed = text.trim().replace(/\s+/g, " ")
  if (!trimmed || index < 0 || index >= words.length) return current
  const rest = Object.fromEntries(Object.entries(current).filter(([key]) => key !== String(index)))
  return trimmed === words[index].word ? rest : { ...rest, [index]: trimmed }
}
